<script setup lang="ts">
import {
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPagination,
  TirPmScrollbar,
} from 'tir-components';
import { TirPmTableTh, TirPmTable } from 'tir-pm-table';
import { Bars3BottomLeftIcon, MagnifyingGlassIcon } from 'tir-style-system/icons/outline';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { MonitoringsAdapterService } from '../../adapter-service';
import PerformerTaskDaysRequestModel from '../../models/PerformerTaskDaysRequestModel';
import { useRouter } from 'vue-router';
import PageRequestModel from '../../models/PageRequestModel';
import PerformerTaskDaysResponseItemsModel from '../../models/PerformerTaskDaysResponseItemsModel';
import DailySummariesModel from '../../models/DailySummariesModel';
import { PaginationRequestModel, PaginationResponseModel } from '@/models';
import {
  GetProjectTasksRequestModel,
  MonitoringHeaderFilterModel,
  TaskColumnModel,
  TaskFilterModel,
  TaskModel,
} from '../../models';
import PageResponseModel from '../../models/PageResponseModel';
import GetEmployeesWithAccessToProjectItemModel from '../../models/GetEmployeesWithAccessToProjectItemModel';
import { TaskViewElementEnum } from '../../enums';
import UserModel from '../../models/UserModel';
import GetTasksListMonitoringDaysRequesModel from '../../models/GetTasksListMonitoringDaysRequesModel';
import MonitoringDaysTableInfo from './monitoring-days-table-info.vue';
import MonitoringDaysDrawerTasks from './monitoring-days-drawer-tasks.vue';
import { debounce } from 'tir-pm-debounce';
import { MonitoringsSkeleton } from '../../monitorings-skeleton';

const props = defineProps({
  /**Фильтр мониторинга */
  filter: { type: Object as PropType<MonitoringHeaderFilterModel> },
  /**Начальная дата */
  selectDateStart: { type: Date },
  /**Конечная дата */
  selectDateEnd: { type: Date },
  /**Выбранный исполнитель */
  selectPerformer: { type: Object as PropType<GetEmployeesWithAccessToProjectItemModel> },
});

const emit = defineEmits<{
  /**Получение общего времени работы */
  (e: 'getTotalWorkTime', totalWorkTime: number);
  /**Событие открытия шторки задачи */
  (e: 'openTasksListDrawer', taskId: number);
}>();

/**Адаптер */
const adapter = new MonitoringsAdapterService();

/**Роутер */
const router = useRouter();

/**Страница */
const pageResponse = ref<PageResponseModel>();

/**Текущая страница */
const currentPage = ref<number>();

/**Дефолтное кол-во элементов на странице */
const DEFAULT_PAGE_STEP = 50;

/**Список задач */
const tasks = ref([]);

/**Ссылка на скролл */
const $scrollbar = ref<InstanceType<typeof TirPmScrollbar>>();

/**Ссылка на таблицу */
const $table = ref<HTMLElement>();

/**Данные юзера выбранной задачи  */
const selectUserData = ref({} as UserModel);

/**Данные сводок выбраной задачи  */
const selectDailySummaries = ref({} as DailySummariesModel);

/**Колонки */
const columns: TaskColumnModel[] = [
  new TaskColumnModel({
    Type: TaskViewElementEnum.Namespace,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Queue,
    Name: 'Очередь',
    Width: '9.375rem',
    MinWidth: '9.375rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Number,
    Name: '№',
    Width: '5rem',
    MinWidth: '5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Name,
    Name: 'Название',
    Width: '24.3125rem',
    MinWidth: '24.3125rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Status,
    Name: 'Статус',
    Width: '5.688rem',
    MinWidth: '5.1875rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Priority,
    Name: 'Приоритет',
    Width: '3.625rem',
    MinWidth: '3.625rem',
    Align: 'right',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.EstimateTime,
    Name: 't (оценка)',
    Width: '4.875rem',
    MinWidth: '4.875rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.WorkTime,
    Name: 't (затрачено)',
    Width: '4.875rem',
    MinWidth: '4.875rem',
    Align: 'center',
  }),
];

/**Видимость tir-pm-drawer */
const visibleDrawer = ref(false);
/**Загрузка */
const loading = ref(false);

/**ID проекта */
const projectId = Number(router.currentRoute.value.params.projectId);

/**Кол-во элементов на странице */
const pageStep = ref(DEFAULT_PAGE_STEP);

/**Колонки таблицы */
const columnTableSetting = ref(['minmax(14.438rem, 14.438rem)', 'minmax(6.713rem, 6.713rem)']);

/**Данные юзера */
const dataUsers = ref<PerformerTaskDaysResponseItemsModel[]>([]);

/**Данные по дням */
const dailySummaries = ref<DailySummariesModel[]>([]);

/**Итоговое время юзера */
const timeSpentUser = ref<number>();

/**Кол-во страниц */
const pagesCount = computed(
  () => pageResponse.value && Math.ceil(pageResponse.value.Count / pageResponse.value.Size),
);

const getColumnWidths = computed(() => {
  return [
    ...columnTableSetting.value,
    ...dailySummaries.value.map(() => 'minmax(6.213rem, 6.213rem)'),
  ];
});

/** Получение списка сотрудников и их затраченное время на задачи по дням */
const getPerformerTaskDaysReport = async (page: PageRequestModel) => {
  loading.value = true;
  const response = await adapter.getPerformerTaskDaysReportAsync(
    new PerformerTaskDaysRequestModel({
      Search: props.filter.Search,
      ProjectId: props.filter.AllMyProjectsChecked ? null : projectId,
      DateStart: props.selectDateStart,
      DateEnd: props.selectDateEnd,
      Page: page,
      PerformerIds: props.selectPerformer.UserId ? [props.selectPerformer.UserId] : [],
    }),
  );
  if (response.IsSuccess) {
    dataUsers.value = response.Value.Items;
    pageResponse.value = response.Value.Page;
    currentPage.value = response.Value.Page.Current;
    dailySummaries.value = response.Value.Items.map((item) => item.DailySummaries)[0] || [];

    emit('getTotalWorkTime', response.Value.TotalWorkTime);
    setTimeout(() => $scrollbar.value?.$scroll.scrollTo($table.value.offsetWidth, 0));
  } else {
    TirPmNotificationEventBus.emit(
      'add',
      new TirPmNotificationModel({
        title: response.ErrorMessage ?? 'Ошибка получения списка задач',
        type: 'error',
        group: 'general',
        life: 5000,
        closable: true,
      }),
    );
  }
  loading.value = false;
};

/**Поиск юзеров */
const seatchUsers = debounce(
  () =>
    getPerformerTaskDaysReport(
      new PageRequestModel({
        Skip: 0,
        Take: pageStep.value,
      }),
    ),
  300,
);
/** Получить список задач */
const getTasks = async (request: GetTasksListMonitoringDaysRequesModel) => {
  const response = await adapter.getTasksByProjectId(
    new GetProjectTasksRequestModel({
      ProjectId: props.filter.AllMyProjectsChecked ? null : projectId,
      Filter: new TaskFilterModel({
        Performers: request.SelectedPerformerId,
      }),
      DateStart: request.SelectDateStart || props.selectDateStart,
      DateEnd: request.SelectDateEnd || props.selectDateEnd,
      IsFilterTimerByPerformerIds: true,
      Page: request.Page,
    }),
  );
  if (response.IsSuccess) {
    tasks.value = response?.Items || [];
  } else {
    TirPmNotificationEventBus.emit(
      'add',
      new TirPmNotificationModel({
        title: response.ErrorMessage ?? 'Ошибка получения списка задач',
        type: 'error',
        group: 'general',
        life: 5000,
        closable: true,
      }),
    );
  }

  return response.IsSuccess;
};

watch(
  () => [props.selectDateStart, props.selectDateEnd],
  () => {
    getPerformerTaskDaysReport(
      new PageRequestModel({
        Skip: 0,
        Take: pageStep.value,
      }),
    );
  },
);
/**Отслеживание изменения строки поиска */
watch(
  () => props.filter.Search,
  () => seatchUsers(),
);
/**Отслеживание изменения состояния чекбокса Все мои проекты */
watch(
  () => props.filter.AllMyProjectsChecked,
  () => {
    getPerformerTaskDaysReport(
      new PageRequestModel({
        Skip: 0,
        Take: pageStep.value,
      }),
    );
  },
);

/**Изменить кол-во элементов на странице */
const changePageStep = async (value: number) => {
  pageStep.value = value;
  await getPerformerTaskDaysReport(
    new PageRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};

/**Обновить текущую страницу */
const changeCurrentPage = async (value: number) => {
  if (!pageResponse.value || currentPage.value === value) return;

  await getPerformerTaskDaysReport(
    new PageRequestModel({
      Skip: pageResponse.value.Size * (value - 1),
      Take: pageStep.value,
    }),
  );
};

/**Закрыть дравер */
const closeDrawer = () => {
  visibleDrawer.value = false;
};

const openDrawer = () => {
  visibleDrawer.value = true;
};

/**Обновить видимость дравера */
const onUpdateVisibleDrawer = (value: boolean) => {
  visibleDrawer.value = value;
};

/**Открыть шторку с задачами */
const openTasksListDrawer = async (
  user: PerformerTaskDaysResponseItemsModel,
  daily: DailySummariesModel,
) => {
  const startDate = new Date(daily.Date);
  startDate.setUTCHours(0, 0, 0, 0);
  startDate.setUTCDate(startDate.getUTCDate());

  const endDate = new Date(startDate);
  endDate.setUTCDate(startDate.getUTCDate() + 1);
  endDate.setUTCHours(0, 0, 0, 0);

  await getTasks(
    new GetTasksListMonitoringDaysRequesModel({
      Page: new PaginationRequestModel({
        Skip: 0,
        Take: pageStep.value,
      }),
      SelectedPerformerId: [user.UsersData.Id],
      SelectDateStart: startDate,
      SelectDateEnd: endDate.toISOString().split('Z')[0],
    }),
  );

  selectUserData.value = user.UsersData;

  const timeSpent = user.DailySummaries.find(
    (d) =>
      new Date(d.Date).toISOString().split('T')[0] ===
      new Date(daily.Date).toISOString().split('T')[0],
  )?.TotalTimeSpent;

  timeSpentUser.value = timeSpent;
  selectDailySummaries.value = daily;

  onUpdateVisibleDrawer(true);
};

/**Отфильтрованные колонки */
const filteredDailySummaries = computed(() => {
  const uniqueDates = new Set();

  return dailySummaries.value.filter((item) => {
    const formattedDate = new Date(item.Date).toISOString().split('T')[0];

    if (uniqueDates.has(formattedDate)) {
      return false;
    }

    uniqueDates.add(formattedDate);
    return true;
  });
});

/**Получить день недели */
const getDayWeek = (date: Date) => {
  let newDate = new Date(date);
  newDate.setDate(newDate.getDate());

  const dayOfWeek = newDate.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

  return {
    day: newDate.toLocaleDateString('ru-RU', { weekday: 'short' }).toUpperCase(),
    isWeekend,
  };
};

const computedDayData = computed(() => {
  const sortedData = filteredDailySummaries.value
    .map((daily) => {
      const dayData = getDayWeek(daily.Date);
      return {
        ...daily,
        isWeekend: dayData.isWeekend,
        day: dayData.day,
      };
    })
    .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());

  return sortedData;
});

onMounted(async () => {
  await getPerformerTaskDaysReport(
    new PageRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
});
</script>

<template>
  <div v-if="!loading" class="monitoring-days-table">
    <TirPmScrollbar
      ref="$scrollbar"
      class="monitoring-days-table__scroll"
      show-on-hover
      v-if="dataUsers.length"
    >
      <div ref="$table">
        <TirPmTable :columns="getColumnWidths" :isBordered="false">
          <template #tableHead>
            <TirPmTableTh class="monitoring-days-table__main-column" :isLeftBorder="false">
              Сотрудник
              <Bars3BottomLeftIcon class="monitoring-days-table__icon" />
            </TirPmTableTh>
            <TirPmTableTh
              class="monitoring-days-table__main-column general-hours-column"
              isLeftBorder
            >
              Общ. часы
            </TirPmTableTh>
            <TirPmTableTh
              v-for="daily in computedDayData"
              class="monitoring-days-table__days-work-column"
            >
              <div
                class="monitoring-days-table__day-week-work"
                :class="{ 'weekend-day': daily.isWeekend }"
              >
                {{ daily.day }}
              </div>
              <div class="monitoring-days-table__day-work">
                {{ new Date(daily.Date).getDate() }}
              </div>
              <div class="monitoring-days-table__day-month-work">
                {{
                  new Date(daily.Date).toLocaleString('ru-RU', { month: 'short' }).replace('.', '')
                }}
              </div>
            </TirPmTableTh>
          </template>
          <template #tableBody>
            <MonitoringDaysTableInfo
              :filter="filter"
              :dataUsers="dataUsers"
              :columnWidths="getColumnWidths"
              :dailySummaries="dailySummaries"
              @EventOpenTasksListDrawer="openTasksListDrawer"
            />
            <MonitoringDaysDrawerTasks
              :visibleDrawer="visibleDrawer"
              :selectUserData="selectUserData"
              :selectDailySummaries="selectDailySummaries"
              :tasks="tasks"
              :columns="columns"
              :timeSpentUser="timeSpentUser"
              @EventCloseDrawer="closeDrawer"
              @EventOpenDrawer="openDrawer"
              @EventonUpdateVisibleDrawer="onUpdateVisibleDrawer"
            />
          </template>
        </TirPmTable>
      </div>
    </TirPmScrollbar>
    <div v-if="!dataUsers.length && !loading" class="monitoring-days-table__no-find-users">
      <TirPmIconView :size="80" :isBordered="false">
        <template #mainSlot>
          <MagnifyingGlassIcon />
        </template>
      </TirPmIconView>
      <div class="monitoring-days-table__no-find-users-title">Данные не найдены</div>
    </div>
    <TirPmPagination
      v-if="pageResponse?.Current && pagesCount"
      class="monitoring-days-table__paginator"
      showMoreElements
      :model-value="pageResponse?.Current"
      :range-size="3"
      :pages="pagesCount"
      :options="[10, 20, 30, 40, 50, 100]"
      :count-elements="pageStep"
      @update:count-elements="changePageStep"
      @update:model-value="changeCurrentPage"
    />
  </div>
  <div v-else>
    <MonitoringsSkeleton />
  </div>
</template>

<style scoped lang="scss">
.weekend-day {
  color: var(--avatar-pale-crimson, #f29cbb) !important;
}
.monitoring-days-table {
  height: 100%;
  &__drawer-content-user-date {
    color: var(--text-secondary, #686c73);
  }

  &__no-find-users {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    height: 100%;
  }
  &__no-find-users-title {
    font-size: 1.625rem;
    font-weight: 500;
  }

  &__paginator {
    position: absolute;
    display: flex;
    justify-content: space-between;
    width: 100%;
    bottom: 0;
    margin-top: 1rem;
  }
  &__main-column {
    display: flex;
    position: sticky !important;
    left: 0;
    z-index: 1;
    align-items: center;
    gap: 0.75rem;
    background-color: var(--neutral-05);
    padding-left: 0.75rem !important;
  }
  &__main-column:last-child {
    padding-left: 0.55rem !important;
  }

  &__icon {
    width: 1rem;
    height: 1rem;
  }

  &__days-work-column {
    gap: 0.25rem !important;
    &::before {
      background-color: var(--neutral-05) !important;
      height: 100%;
      margin-top: -4px;
    }
  }

  &__day-month-work {
    overflow: hidden;
    text-overflow: ellipsis !important;
  }

  &__day-week-work {
    margin-left: 0.2813rem;
    color: var(--text-tertiary, #93969b);
  }
  &__day-work {
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
  }
}

.general-hours-column {
  position: sticky !important;
  left: 14.438rem;
  border-right: 1px solid var(--neutral-20, #d4d5d7);
  &::before {
    height: 100%;
    margin-top: -4px;
  }
}
</style>

<style lang="scss">
.monitoring-days-table {
  &__drawer {
    z-index: 1000 !important;
  }
  &__scroll {
    height: calc(100% - 2.3rem);
    // background: linear-gradient(to bottom, #f4f4f5, #ffffff);
    .hide-scroll {
      height: 100%;
    }
  }
}

.monitoring-days__table .table-grid .table-grid-header {
  gap: 0;
  padding: 0.75rem 0.75rem 0.75rem 0;
  padding-bottom: 0;
}

.monitoring-days__table .tir-scroll .hide-scroll .table-container {
  min-width: max-content;
}
</style>
